import { Component, Inject, OnDestroy, OnInit, ViewChild, NgZone, AfterViewInit, HostListener } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subscription, Subject } from 'rxjs';
import { debounceTime, throttleTime } from 'rxjs/operators';
import { GoogleMap } from '@angular/google-maps';
import { trackVehicleImages } from 'src/@order/commonData/commonUiData';
import { liveTraking, ordersTracking } from 'src/app/utils/systemEnums';
import { OrderCompleteDialogComponent } from 'src/@order/dialogs/order-complete-dialog/order-complete-dialog.component';

@Component({
  selector: 'app-track-driver',
  templateUrl: './track-driver.component.html',
  styleUrls: ['./track-driver.component.scss']
})
export class TrackDriverComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('map') map: GoogleMap;

  heading: string;
  order: any;

  // UI details
  distanceRemainingKm: number = 0;
  estimatedTime: string = 'N/A';
  isDistanceUpdating: boolean = true;
  orderStatusText: string = 'N/A';
  trackingIdText: string = 'N/A';
  driverNameText: string = 'N/A';
  driverProfileImageUrl: string | null = null;
  dir: any = {
    destination: null,
    origin: null,
    bearing: 0
  };

  // Order status properties
  isOrderAccepted: boolean = false;
  isOrderPickedUp: boolean = false;
  waypoints: any[] = [];
  routePath: google.maps.LatLngLiteral[] = [];
  remainingDropCoords: google.maps.LatLngLiteral[] = [];
  mapCenter: google.maps.LatLngLiteral = { lat: 0, lng: 0 };
  zoom = 15; // Increased default zoom for better vehicle positioning accuracy
  private minZoom = 14; // Minimum zoom to keep vehicle accurately on road
  private maxZoom = 18; // Maximum zoom to prevent over-zooming

  // Use a small threshold for detecting significant movement
  private routeUpdateThreshold = 0.05; // 30 meters
  private lastRouteUpdatePosition: google.maps.LatLngLiteral | null = null;

  // Use subjects for smoother updates
  private locationUpdateSubject = new Subject<google.maps.LatLngLiteral>();
  private routeUpdateSubject = new Subject<void>();

  routeBuilt = false;
  driverIcon = {
    url: '',
    scaledSize: new google.maps.Size(60, 60),
    anchor: new google.maps.Point(20, 20)
  };

  private driverSubscription: Subscription;
  private dropPointsSubscription: Subscription;
  private locationUpdateSubscription: Subscription;
  private routeUpdateSubscription: Subscription;
  private lastKnownPosition: google.maps.LatLngLiteral | null = null;
  private directionsService: google.maps.DirectionsService;
  private routeRenderInProgress = false;

  private positionQueue: google.maps.LatLngLiteral[] = [];
  private isAnimating = false;


  private gradientPolyline: google.maps.Polyline;
  private animationSpeed = 500;

  private driverMarker: google.maps.marker.AdvancedMarkerElement;
  private animationFrameId: number | null = null;

  markerOptions = {
    origin: {
      icon: {
        url: '',
        anchor: new google.maps.Point(20, 20),
        rotation: 0
      }
    },
    destination: {
      icon: {
        url: 'assets/images/tracking/drop_off.png',
        scaledSize: new google.maps.Size(40, 40)
      }
    },
    pickup: {
      icon: {
        url: 'assets/images/tracking/pickup_icon.png',
        scaledSize: new google.maps.Size(40, 40)
      }
    },
    // waypoints: {
    //   icon: {
    //     url: 'assets/images/tracking/map-marker.png'
    //   }
    // }
  };

  polylineOptions: google.maps.PolylineOptions = {
    strokeColor: '#1E88E5',
    strokeOpacity: 0.9,
    strokeWeight: 5
  };

  private driverIconContainer: HTMLDivElement | null = null;
  private driverIconElement: HTMLImageElement | null = null;
  routePolyline: google.maps.Polyline;
  pickupCoords: { lat: number; lng: number; };


  private originalRoutePath: google.maps.LatLngLiteral[] = [];
  private routeDeviationThreshold = 0.1; // 100 meters - threshold to trigger new route calculation
  private hasRouteDeviation = false;

  private apiCallCount = 0;
  private lastApiCallTime: Date | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public _data: any,
    private afs: AngularFirestore,
    public _matDialogRef: MatDialogRef<TrackDriverComponent>,
    private ngZone: NgZone,
    private _matDialog: MatDialog
  ) {
    this.directionsService = new google.maps.DirectionsService();
  }

  ngOnInit(): void {
    this.heading = this._data.heading;
    this.order = this._data.order;

    // Set order status flags
    const orderStatus = this.order?.deliveryStatus || this.order?.status;

    this.isOrderAccepted = orderStatus === 3;
    this.isOrderPickedUp = orderStatus === 4;
    this.syncOrderUiDetails(this.order);

    // Set up the location update stream with debounce to smooth rapid updates
    this.locationUpdateSubscription = this.locationUpdateSubject
      .pipe(debounceTime(100)) // Process location updates after 100ms of stability
      .subscribe(newLocation => {
        this.processDriverLocationUpdate(newLocation);
      });

    // Set up the route update stream with throttle to prevent too many API calls
    this.routeUpdateSubscription = this.routeUpdateSubject
      .pipe(throttleTime(3000)) // At most one route update every 3 seconds
      .subscribe(() => {
        if (!this.routeRenderInProgress) {
          this.buildRouteToDestination();
        }
      });

    this.dropPointsSubscription = this.updateDropoffPoint(String(this.order.id))
      .subscribe((res: any) => {
        if (res && res.details) {
          // Listen to order changes (picked up, status updates, drop updates)
          this.handleOrderUpdate(res);

          // Set the vehicle image first
          this.setDriverVehicleImage(res.details.vehicleType);

          // Prefer dropPoints if available; fallback to dropArray
          let drops = Array.isArray(res.details.dropPoints) && res.details.dropPoints.length
            ? res.details.dropPoints
            : (res.details.dropArray || []);

          console.log('drops', drops);

          // Filter out already delivered drops (isDelivered == 1)
          drops = drops.filter((point: any) => point?.isDelivered !== 1);

          if (drops && drops.length > 0) {
            const sorted = drops.sort((a: any, b: any) => (a.id > b.id ? 1 : -1));

            // Clear waypoints array to prevent duplicates
            this.waypoints = [];
            this.clearExistingMarkers(); // Clear existing markers before adding new ones
            this.remainingDropCoords = [];



            this.pickupCoords = {
              // Support both old shape (pickup.coordinates.lat/lng) and new (pickup.latitude/longitude)
              lat: Number(
                res.details.pickup.latitude ??
                res.details.pickup.lat ??
                res.details.pickup.coordinates?.latitude
              ),
              lng: Number(
                res.details.pickup.longitude ??
                res.details.pickup.lng ??
                res.details.pickup.coordinates?.longitude
              )
            };



            sorted.forEach((point, i) => {
              const coords = {
                // Support both new fields (latitude/longitude) and legacy coordinates.{latitude,longitude}
                lat: Number(point.latitude ?? point.lat ?? point.coordinates?.latitude),
                lng: Number(point.longitude ?? point.lng ?? point.coordinates?.longitude)
              };

              // Track remaining (not delivered) drop coordinates for distance calculations
              this.remainingDropCoords.push(coords);

              if (i === sorted.length - 1) {
                this.dir.destination = coords;
              } else {
                this.waypoints.push({
                  location: coords,
                  stopover: true
                });
              }

              // Create a marker for each drop point only when order is picked up
              if (this.isOrderPickedUp) {
                console.log('Creating destination marker for drop point:', coords);
                this.createMarker(coords, 'assets/images/tracking/drop_off.png');
              } else {
                console.log('Skipping destination marker creation - order not picked up yet');
              }
            });

            // Subscribe to driver location after setting up waypoints and destination
            this.subscribeToDriverLocation();
          }
        }
      });
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    // Update the route display and map view on resize
    if (this.routeBuilt) {
      this.updateRoutePolyline();
      this.updateInitialMapView();
    }
  }

  ngAfterViewInit(): void {
    if (this.map?.googleMap) {
      setTimeout(() => {
        const mapOptions: google.maps.MapOptions = {
          zoom: this.zoom,
          center: this.mapCenter,
          mapId: 'd6c938ddd2307338',
          gestureHandling: 'cooperative',
          zoomControl: true,
          disableDefaultUI: false,
          clickableIcons: false, // Disable POI clicks for better performance
          mapTypeControl: false  // Disable map type control for cleaner UI
        };

        const map = this.map.googleMap;
        map.setOptions(mapOptions);

        // Remove any existing marker if it exists
        if (this.driverMarker) {
          this.driverMarker.map = null;
        }

        // Create container once
        this.driverIconContainer = document.createElement('div');
        this.driverIconContainer.style.position = 'relative';
        this.driverIconContainer.style.width = '60px';
        this.driverIconContainer.style.height = '60px';

        // Create image once
        this.driverIconElement = document.createElement('img');
        this.driverIconElement.style.width = '60px';
        this.driverIconElement.style.height = '60px';
        this.driverIconElement.style.transition = 'transform 0.5s ease';
        this.driverIconElement.style.display = 'block';
        this.driverIconElement.style.position = 'absolute';
        this.driverIconElement.style.top = '0';
        this.driverIconElement.style.left = '0';
        this.driverIconElement.style.willChange = 'transform'; // Performance optimization

        // Set the initial image URL if available
        if (this.driverIcon.url) {
          this.driverIconElement.src = this.driverIcon.url;
        }

        // Set initial rotation
        const initialRotation = this.dir.bearing || 0;
        console.log("Initial rotation:", initialRotation);

        // this.driverIconElement.style.transform = `rotate(${initialRotation}deg)`;

        // Add image to container
        this.driverIconContainer.appendChild(this.driverIconElement);

        // Create marker with container
        this.driverMarker = new google.maps.marker.AdvancedMarkerElement({
          position: this.dir.origin || { lat: 0, lng: 0 },
          content: this.driverIconContainer,
          map: map,
          zIndex: 1000 // Ensure driver is on top of route
        });
      });

      google.maps.event.addListener(this.map.googleMap, 'zoom_changed', () => {
        this.ngZone.run(() => {
          const currentZoom = this.map.googleMap.getZoom() || this.zoom;
          // Prevent zooming out too far - maintain minimum zoom for accurate vehicle positioning
          if (currentZoom < this.minZoom) {
            this.map.googleMap.setZoom(this.minZoom);
          } else if (currentZoom > this.maxZoom) {
            this.map.googleMap.setZoom(this.maxZoom);
          }
          
          if (this.routeBuilt) {
            this.updateRoutePolyline();
          }
        });
      });

    }
  }

  subscribeToDriverLocation(): void {
    if (this.driverSubscription) {
      this.driverSubscription.unsubscribe();
    }

    this.driverSubscription = this.updateTrackingDoc(String(this.order.driverDetail.id))
      .subscribe((res: any) => {
        if (res?.driverlocation) {
          try {
            const newLocation = {
              lat: Number(res.driverlocation._lat),
              lng: Number(res.driverlocation._long)
            };

            if (isNaN(newLocation.lat) || isNaN(newLocation.lng) ||
              Math.abs(newLocation.lat) > 90 || Math.abs(newLocation.lng) > 180) {
              console.error('Invalid coordinates received:', newLocation);
              return;
            }

            // Set driver vehicle image first if needed
            if (!this.driverIcon.url && res.details?.vehicleType) {
              this.setDriverVehicleImage(res.details.vehicleType);
            }

            // Push to subject for smooth handling
            this.locationUpdateSubject.next(newLocation);
          } catch (error) {
            console.error('Error processing driver location:', error);
          }
        }
      });
  }

  processDriverLocationUpdate(newLocation: google.maps.LatLngLiteral): void {
    if (!this.dir.origin) {
      // First location update
      this.dir.origin = newLocation;
      this.lastKnownPosition = newLocation;
      this.mapCenter = newLocation;
      this.lastRouteUpdatePosition = newLocation;

      if (this.driverMarker) {
        this.driverMarker.position = newLocation;
      }

      // Build initial route based on pickup status
      if ((!this.isOrderPickedUp && this.pickupCoords) || (this.isOrderPickedUp && this.dir.destination)) {
        this.buildRouteToDestination(true); // Force initial route build
      }
    } else {
      // Update driver position
      this.updateDriverPosition(newLocation);
    }

    // Update remaining distance + ETA whenever we get location updates
    this.updateDistanceAndTimeFromCurrentPosition(newLocation);

    // Ensure the marker is properly displayed on the map
    // if (this.driverMarker && !this.driverMarker.map && this.map?.googleMap) {
    //   this.driverMarker.map = this.map.googleMap;
    // }
  }



  buildRouteToDestination(forceUpdate = false): void {
    // Determine the target destination based on pickup status
    let targetDestination;
    if (!this.isOrderPickedUp && this.pickupCoords) {
      // If order is not picked up, route to pickup location
      targetDestination = this.pickupCoords;
    } else if (this.isOrderPickedUp && this.dir.destination) {
      // If order is picked up, route to destination
      targetDestination = this.dir.destination;
    } else {
      console.warn('Cannot build route: missing origin or target destination');
      return;
    }

    if (!this.dir.origin || !targetDestination) {
      console.warn('Cannot build route: missing origin or target destination');
      return;
    }

    // Log route check
    console.log(`[Route Check] Force update: ${forceUpdate}, Has deviation: ${this.hasRouteDeviation}, Original path points: ${this.originalRoutePath.length}`);

    // If we already have a route and this isn't a forced update, check for deviation
    if (this.originalRoutePath.length > 0 && !forceUpdate && !this.hasRouteDeviation) {
      const deviationDistance = this.calculateDistanceFromRoute(this.dir.origin);
      console.log(`[Deviation Check] Distance from route: ${deviationDistance.toFixed(4)} km, Threshold: ${this.routeDeviationThreshold} km`);

      if (deviationDistance < this.routeDeviationThreshold) {
        console.log('[Route Update] Driver on route - updating polyline only');
        this.updateRoutePolyline();
        return;
      } else {
        this.hasRouteDeviation = true;
        console.log('[Route Update] Significant deviation detected - recalculating route');
      }
    }

    this.routeRenderInProgress = true;
    this.apiCallCount++;
    this.lastApiCallTime = new Date();

    console.log(`[API Call #${this.apiCallCount}] Initiating Directions API request at ${this.lastApiCallTime.toISOString()}`);
    console.log(`[API Params] Origin: ${this.dir.origin.lat},${this.dir.origin.lng} | Target: ${targetDestination.lat},${targetDestination.lng} | Waypoints: ${this.waypoints.length}`);
    console.log(`[Route Type] ${!this.isOrderPickedUp ? 'To Pickup' : 'To Destination'}`);

    // Determine waypoints based on pickup status
    let routeWaypoints = [];
    if (this.isOrderPickedUp) {
      // If picked up, use all waypoints to destination
      routeWaypoints = this.waypoints;
    }
    // If not picked up, no waypoints (direct to pickup)

    this.directionsService.route({
      origin: this.dir.origin,
      destination: targetDestination,
      waypoints: routeWaypoints,
      optimizeWaypoints: false,
      travelMode: google.maps.TravelMode.DRIVING,
      provideRouteAlternatives: false,
      avoidFerries: true,
      avoidHighways: false,
      avoidTolls: false
    }, (response, status) => {
      const callEndTime = new Date();
      const duration = this.lastApiCallTime ? callEndTime.getTime() - this.lastApiCallTime.getTime() : 0;

      this.ngZone.run(() => {
        if (status === 'OK' && response.routes.length > 0) {
          console.log(`[API Success #${this.apiCallCount}] Completed in ${duration}ms with ${response.routes[0].legs.length} legs`);

          this.routePath = [];
          this.originalRoutePath = [];

          const totalPoints = response.routes[0].legs.reduce((acc, leg) => {
            return acc + leg.steps.reduce((stepAcc, step) => stepAcc + step.path.length, 0);
          }, 0);

          console.log(`[Route Details] Total path points: ${totalPoints}`);

          response.routes[0].legs.forEach(leg => {
            leg.steps.forEach(step => {
              step.path.forEach(point => {
                this.routePath.push({ lat: point.lat(), lng: point.lng() });
                this.originalRoutePath.push({ lat: point.lat(), lng: point.lng() });
              });
            });
          });

          this.updateRoutePolyline();
          this.lastRouteUpdatePosition = { ...this.dir.origin };
          this.routeBuilt = true;
          this.hasRouteDeviation = false;
        } else {
          console.error(`[API Error #${this.apiCallCount}] Status: ${status} after ${duration}ms`);
          this.createFallbackRoute();
        }

        this.routeRenderInProgress = false;
      });
    });
  }

  private updateRoutePolyline(): void {
    if (!this.map?.googleMap) return;

    // Get current zoom level
    const currentZoom = this.map.googleMap.getZoom();
    const strokeWeight = currentZoom > 15 ? 7 : (currentZoom > 12 ? 5 : 3);

    // Remove existing polylines if they exist
    if (this.routePolyline) {
      this.routePolyline.setMap(null);
    }
    if (this.gradientPolyline) {
      this.gradientPolyline.setMap(null);
    }

    // If we have an original route path, use that as the base
    const basePath = this.originalRoutePath.length > 0 ? this.originalRoutePath : this.routePath;

    // Create new polyline with improved styling
    this.routePolyline = new google.maps.Polyline({
      path: basePath,
      geodesic: true,
      strokeColor: '#1E88E5',
      strokeOpacity: 0.9,
      strokeWeight: strokeWeight,
      map: this.map.googleMap,
      zIndex: 1
    });

    // Create gradient polyline from start to current position
    if (this.dir.origin && basePath.length > 0) {
      // Find the closest point on the route to current position
      let closestPointIndex = 0;
      let closestDistance = Number.MAX_VALUE;

      basePath.forEach((point, index) => {
        const distance = this.calculateDistance(
          this.dir.origin.lat, this.dir.origin.lng,
          point.lat, point.lng
        );

        if (distance < closestDistance) {
          closestDistance = distance;
          closestPointIndex = index;
        }
      });

      // Create gradient path from start to current point
      const gradientPath = basePath.slice(0, closestPointIndex + 1);

      // Only add current position if we're significantly off the route
      if (closestDistance > 0.01) { // 10 meters
        gradientPath.push(this.dir.origin);
      }

      this.gradientPolyline = new google.maps.Polyline({
        path: gradientPath,
        geodesic: true,
        strokeColor: '#4CAF50',
        strokeOpacity: 1.0,
        strokeWeight: strokeWeight,
        map: this.map.googleMap,
        zIndex: 2
      });
    }
  }

  private calculateDistanceFromRoute(point: google.maps.LatLngLiteral): number {
    if (!this.originalRoutePath || this.originalRoutePath.length === 0) {
      return Number.MAX_VALUE;
    }

    let minDistance = Number.MAX_VALUE;

    // Find the closest point on the route
    this.originalRoutePath.forEach(routePoint => {
      const distance = this.calculateDistance(
        point.lat, point.lng,
        routePoint.lat, routePoint.lng
      );
      if (distance < minDistance) {
        minDistance = distance;
      }
    });

    return minDistance;
  }


  updateDriverPosition(newLocation: google.maps.LatLngLiteral): void {
    if (!newLocation || (isNaN(newLocation.lat) || isNaN(newLocation.lng))) {
      console.error("Invalid location received:", newLocation);
      return;
    }

    // Snap to nearest road point if we have a route
    let snappedPosition = this.originalRoutePath.length > 0 ? this.snapToRoute(newLocation) : newLocation;

    this.dir.origin = snappedPosition;

    if (!this.lastKnownPosition) {
      this.lastKnownPosition = snappedPosition;
      this.mapCenter = snappedPosition;
      if (this.driverMarker) {
        this.driverMarker.position = snappedPosition;
      }
      return;
    }

    const distance = this.calculateDistance(
      this.lastKnownPosition.lat, this.lastKnownPosition.lng,
      snappedPosition.lat, snappedPosition.lng
    );

    if (distance < 0.0005) {
      return;
    }

    // Calculate bearing using snapped positions
    const bearing = this.calculateBearing(
      this.lastKnownPosition.lat, this.lastKnownPosition.lng,
      snappedPosition.lat, snappedPosition.lng
    );

    this.dir.bearing = bearing;

    // Update rotation immediately
    if (this.driverIconElement) {
      this.driverIconElement.style.transform = `rotate(${bearing}deg)`; // Ensure the icon rotates to the correct direction
    }

    this.positionQueue.push(snappedPosition);

    if (!this.isAnimating) {
      this.processNextPositionInQueue();
    }

    if (distance > this.routeUpdateThreshold) {
      this.routeUpdateSubject.next();
    }
  }

  /**
   * Listener for order updates from firestore doc (ordersTracking).
   * Keeps local order/status flags in sync and forces route refresh when picked up.
   */
  private handleOrderUpdate(orderDoc: any): void {
    const orderData = orderDoc;

    const nextStatus =
      orderData?.details?.status ??
      orderData?.deliveryStatus ??
      orderData?.status;


    const isDriverCompleted = orderData?.details?.isDriverComplete;

    const prevPickedUp = this.isOrderPickedUp;
    const prevAccepted = this.isOrderAccepted;

    // If order is completed (status 5), close tracking and show success dialog
    if (nextStatus === 5 || isDriverCompleted == 2) {
      this.ngZone.run(() => {
        // Close the tracking dialog
        this._matDialogRef.close();

        // Show success dialog matching the provided design
        this._matDialog.open(OrderCompleteDialogComponent, {
          panelClass: ['commonDialogWrapper'],
          backdropClass: 'dialogBackdrop',
          autoFocus: false,
          disableClose: false,
          width: '400px',
          data: {
            heading: 'Order Completed!',
            message: 'Your order has been completed successfully.',
          }
        });
      });
      return;
    }

    this.isOrderAccepted = nextStatus === 3;
    this.isOrderPickedUp = nextStatus === 4;

    // Keep the "order" reference updated for UI
    this.order = {
      ...(this.order || {}),
      ...(orderData || {}),
      details: orderData?.details ?? this.order?.details
    };

    this.syncOrderUiDetails(this.order);

    // If the order just switched to picked up, force route recalculation and marker refresh
    if (!prevPickedUp && this.isOrderPickedUp) {
      console.log('Order picked up - forcing route rebuild and markers refresh');
      this.hasRouteDeviation = true;
      this.routeBuilt = false;

      // Clear any old markers and rebuild markers for destinations/waypoints
      this.clearExistingMarkers();

      // Destination/waypoints are already set from dropArray in the same subscription;
      // but we force a route update once the state changes.
      if (this.dir.origin && this.dir.destination) {
        this.buildRouteToDestination(true);
      }
    }

    // If accepted/picked-up flags changed, force a route refresh (covers edge cases)
    if (prevPickedUp !== this.isOrderPickedUp || prevAccepted !== this.isOrderAccepted) {
      this.hasRouteDeviation = true;
    }
  }


  private snapToRoute(point: google.maps.LatLngLiteral): google.maps.LatLngLiteral {
    if (!this.originalRoutePath || this.originalRoutePath.length === 0) {
      return point;
    }

    let closestPoint = this.originalRoutePath[0];
    let closestDistance = this.calculateDistanceMeters(
      point.lat, point.lng,
      closestPoint.lat, closestPoint.lng
    );

    for (const routePoint of this.originalRoutePath) {
      const distance = this.calculateDistanceMeters(
        point.lat, point.lng,
        routePoint.lat, routePoint.lng
      );
      if (distance < closestDistance) {
        closestDistance = distance;
        closestPoint = routePoint;
      }
    }

    // Only snap if reasonably close to the route (30 meters threshold for better accuracy)
    return closestDistance < 30 ? closestPoint : point;
  }



  private processNextPositionInQueue(): void {
    if (this.positionQueue.length === 0) {
      this.isAnimating = false;
      return;
    }

    this.isAnimating = true;

    // Get the next position from the queue
    const newPosition = this.positionQueue.shift();

    // Calculate bearing for vehicle rotation
    const bearing = this.calculateBearing(
      this.lastKnownPosition.lat, this.lastKnownPosition.lng,
      newPosition.lat, newPosition.lng
    );

    this.dir.bearing = bearing;
    if (this.markerOptions.origin.icon) {
      this.markerOptions.origin.icon.rotation = bearing;
    }


    console.log("helo in animateMarkerMovement");

    // Animate to this position
    this.animateMarkerMovement(this.lastKnownPosition, newPosition, bearing);
  }


  animateMarkerMovement(start: google.maps.LatLngLiteral, end: google.maps.LatLngLiteral, bearing: number): void {
    // Cancel any ongoing animation
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    const startTime = performance.now();
    const duration = 1000;
    const startBearing = this.dir.bearing;
    const bearingDiff = this.getShortestRotation(startBearing, bearing);

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeProgress = this.easeInOutQuintic(progress);

      // console.log("currentTime , startTime, progress  ", currentTime, startTime, progress);

      const interpolatedLat = start.lat + (end.lat - start.lat) * easeProgress;
      const interpolatedLng = start.lng + (end.lng - start.lng) * easeProgress;
      const interpolatedBearing = startBearing + bearingDiff * easeProgress;




      this.ngZone.run(() => {
        // Update marker position and rotation
        if (this.driverMarker && this.driverIconElement) {
          this.driverMarker.position = { lat: interpolatedLat, lng: interpolatedLng };

          // IMPORTANT FIX: Apply rotation correctly and consistently
          const rotationTransform = `rotate(${interpolatedBearing}deg)`;

          // // Add subtle scale effect during movement
          // const scaleFactor = 1 + Math.sin(progress * Math.PI) * 0.05; // 5% scale effect

          // // Apply both transformations together
          // this.driverIconElement.style.transform = `${rotationTransform}`;

          // Add subtle shadow effect during movement
          this.driverIconElement.style.filter = 'drop-shadow(0px 2px 3px rgba(0,0,0,0.3))';
        }

        // Handle map view adjustments if needed
        if (this.map && this.map.googleMap) {
          const currentZoom = this.map.googleMap.getZoom() || this.zoom;
          
          // Ensure minimum zoom level for accurate vehicle positioning
          if (currentZoom < this.minZoom) {
            this.map.googleMap.setZoom(this.minZoom);
          }

          const bounds = this.map.googleMap.getBounds();
          if (bounds) {
            const ne = bounds.getNorthEast();
            const sw = bounds.getSouthWest();

            const buffer = 0.25; // Keep driver in center 75% of screen (was 0.3)
            const viewWidth = ne.lng() - sw.lng();
            const viewHeight = ne.lat() - sw.lat();

            const minLng = sw.lng() + viewWidth * buffer;
            const maxLng = ne.lng() - viewWidth * buffer;
            const minLat = sw.lat() + viewHeight * buffer;
            const maxLat = ne.lat() - viewHeight * buffer;

            if (interpolatedLat < minLat || interpolatedLat > maxLat ||
              interpolatedLng < minLng || interpolatedLng > maxLng) {
              // Use smooth pan effect and ensure zoom level
              this.map.googleMap.panTo({ lat: interpolatedLat, lng: interpolatedLng });
              // Ensure zoom is at least minimum level after panning
              setTimeout(() => {
                const zoomAfterPan = this.map.googleMap.getZoom() || this.zoom;
                if (zoomAfterPan < this.minZoom) {
                  this.map.googleMap.setZoom(this.minZoom);
                }
              }, 100);
            }
          }
        }
      });

      if (progress < 1) {
        this.animationFrameId = requestAnimationFrame(animate);
      } else {
        this.animationFrameId = null;
        this.lastKnownPosition = end;

        // IMPORTANT FIX: Update bearing consistently
        this.dir.bearing = bearing;

        console.log("hello in the else part", this.dir.bearing);


        // // IMPORTANT FIX: Ensure final rotation is applied correctly
        if (this.driverIconElement) {
          // this.driverIconElement.style.transform = `rotate(${bearing}deg)`;
          this.driverIconElement.style.filter = '';
        }

        // Update the gradient polyline
        this.updateRoutePolyline();

        // Process the next position in the queue
        this.processNextPositionInQueue();
      }

    };

    this.animationFrameId = requestAnimationFrame(animate);
  }

  private easeInOutQuintic(t: number): number {
    return t < 0.5
      ? 16 * t * t * t * t * t
      : 1 - Math.pow(-2 * t + 2, 5) / 2;
  }

  // Cubic easing for smoother animations
  easeInOutCubic(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  // Get shortest rotation path (clockwise or counterclockwise)
  getShortestRotation(startAngle: number, endAngle: number): number {
    let diff = endAngle - startAngle;
    if (diff > 180) diff -= 360;
    if (diff < -180) diff += 360;
    return diff;
  }

  createFallbackRoute(): void {
    if (!this.dir.origin || !this.dir.destination) return;

    const routePoints = [this.dir.origin];
    this.waypoints.forEach(wp => {
      routePoints.push(wp.location);
    });
    routePoints.push(this.dir.destination);

    this.routePath = routePoints;
    this.routeBuilt = true;
    this.updateRoutePolyline();
    this.updateInitialMapView();
  }

  updateInitialMapView(): void {
    if (!this.routePath || this.routePath.length === 0 || !this.map?.googleMap) {
      return;
    }

    // Create bounds that include all route points
    const bounds = new google.maps.LatLngBounds();

    // Add origin
    if (this.dir.origin) {
      bounds.extend(new google.maps.LatLng(this.dir.origin.lat, this.dir.origin.lng));
    }

    // Add target destination based on pickup status
    if (!this.isOrderPickedUp && this.pickupCoords) {
      // If not picked up, include pickup location
      bounds.extend(new google.maps.LatLng(this.pickupCoords.lat, this.pickupCoords.lng));
    } else if (this.isOrderPickedUp && this.dir.destination) {
      // If picked up, include destination and waypoints
      bounds.extend(new google.maps.LatLng(this.dir.destination.lat, this.dir.destination.lng));

      // Add waypoints only when picked up
      this.waypoints.forEach(waypoint => {
        bounds.extend(new google.maps.LatLng(waypoint.location.lat, waypoint.location.lng));
      });
    }

    // Add route path points
    this.routePath.forEach(point => {
      bounds.extend(new google.maps.LatLng(point.lat, point.lng));
    });

    // Apply bounds with padding, but ensure minimum zoom for accurate positioning
    this.map.googleMap.fitBounds(bounds, 50); // 50 pixels padding
    
    // Ensure zoom level is within acceptable range after fitting bounds
    setTimeout(() => {
      const currentZoom = this.map.googleMap.getZoom() || this.zoom;
      if (currentZoom < this.minZoom) {
        this.map.googleMap.setZoom(this.minZoom);
      } else if (currentZoom > this.maxZoom) {
        this.map.googleMap.setZoom(this.maxZoom);
      }
    }, 100);
  }

  private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371; // Earth radius in km
    const dLat = this.toRad(lat2 - lat1);
    const dLon = this.toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  /**
   * Meter-based distance (matches provided function signature/units).
   */
  private calculateDistanceMeters(lat1: number, lon1: number, lat2: number, lon2: number): number {
    if (!lat1 || !lon1 || !lat2 || !lon2) return Infinity;
    const R = 6371e3;
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  /**
   * Calculates remaining distance (km) + ETA string.
   * Primary strategy: distance to nearest remaining drop-off.
   * Fallback: distance along remaining polyline if no drop coords available.
   */
  private updateDistanceAndTimeFromCurrentPosition(currentPosition: google.maps.LatLngLiteral | null): void {
    if (!currentPosition) {
      this.isDistanceUpdating = true;
      this.distanceRemainingKm = 0;
      this.estimatedTime = 'Updating';
      return;
    }

    // 1) Prefer distance to nearest remaining drop-off (straight line)
    if (this.remainingDropCoords && this.remainingDropCoords.length > 0) {
      let nearestMeters = Number.MAX_VALUE;

      this.remainingDropCoords.forEach(point => {
        const d = this.calculateDistanceMeters(
          currentPosition.lat,
          currentPosition.lng,
          point.lat,
          point.lng
        );
        if (d < nearestMeters) {
          nearestMeters = d;
        }
      });

      const kmRemainingDirect = Math.round((nearestMeters / 1000) * 10) / 10;
      this.distanceRemainingKm = isFinite(kmRemainingDirect) ? kmRemainingDirect : 0;
      this.isDistanceUpdating = false;
    } else {
      // 2) Fallback: approximate along remaining route polyline
      const basePath = (this.originalRoutePath && this.originalRoutePath.length > 1)
        ? this.originalRoutePath
        : this.routePath;

      if (!basePath || basePath.length < 2) {
        this.isDistanceUpdating = true;
        this.distanceRemainingKm = 0;
        this.estimatedTime = 'Updating';
        return;
      }

      // Find closest point on route to current position
      let closestPointIndex = 0;
      let closestDistanceMeters = Number.MAX_VALUE;

      basePath.forEach((point, index) => {
        const d = this.calculateDistanceMeters(
          currentPosition.lat,
          currentPosition.lng,
          point.lat,
          point.lng
        );
        if (d < closestDistanceMeters) {
          closestDistanceMeters = d;
          closestPointIndex = index;
        }
      });

      const remainingRoute = basePath.slice(closestPointIndex);
      if (remainingRoute.length < 2) {
        this.isDistanceUpdating = true;
        this.distanceRemainingKm = 0;
        this.estimatedTime = 'Updating';
        return;
      }

      // Calculate total remaining distance along the route
      let totalDistanceMeters = 0;
      for (let i = 0; i < remainingRoute.length - 1; i++) {
        totalDistanceMeters += this.calculateDistanceMeters(
          remainingRoute[i].lat,
          remainingRoute[i].lng,
          remainingRoute[i + 1].lat,
          remainingRoute[i + 1].lng
        );
      }

      // Add distance from current position to first point in remaining route
      totalDistanceMeters += this.calculateDistanceMeters(
        currentPosition.lat,
        currentPosition.lng,
        remainingRoute[0].lat,
        remainingRoute[0].lng
      );

      const kmRemainingRoute = Math.round((totalDistanceMeters / 1000) * 10) / 10;
      this.distanceRemainingKm = isFinite(kmRemainingRoute) ? kmRemainingRoute : 0;
      this.isDistanceUpdating = false;
    }
    this.isDistanceUpdating = false;

    // Calculate estimated time based on average speed (50 km/h as default)
    const averageSpeedKmph = 50;
    const hours = this.distanceRemainingKm / averageSpeedKmph;
    const minutes = Math.round(hours * 60);

    // Format the time string
    let timeString = '';
    if (minutes < 1) {
      timeString = 'Less than a minute';
    } else if (minutes < 60) {
      timeString = `${minutes} min${minutes !== 1 ? 's' : ''}`;
    } else {
      const hoursPart = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      timeString = `${hoursPart} hr${hoursPart !== 1 ? 's' : ''}`;
      if (remainingMinutes > 0) {
        timeString += ` ${remainingMinutes} min${remainingMinutes !== 1 ? 's' : ''}`;
      }
    }

    this.estimatedTime = timeString || 'N/A';
  }

  private syncOrderUiDetails(order: any): void {
    // Get tracking ID - priority: order_id (from API), then id, then details.trackingId
    const trackingId = order?.order_id ?? order?.trackingId ?? order?.tracking_id ?? order?.id ?? order?.details?.trackingId;
    const status = order?.details?.status ?? order?.deliveryStatus ?? order?.status;

    this.trackingIdText = trackingId ? `${trackingId}` : 'N/A';
    this.orderStatusText = status !== undefined && status !== null ? `${status}` : 'N/A';

    // Get driver name - combine firstName and lastName from driverDetail
    let driverName = 'N/A';
    if (order?.driverDetail) {
      const firstName = order.driverDetail.firstName || '';
      const lastName = order.driverDetail.lastName || '';
      if (firstName || lastName) {
        driverName = `${firstName} ${lastName}`.trim();
      } else {
        // Fallback to other possible fields
        driverName = order.driverDetail.name ?? order.driverDetail.fullName ?? order?.details?.driverName ?? 'N/A';
      }
    } else {
      driverName = order?.details?.driverName ?? 'N/A';
    }

    // Get driver image - priority: driverDetail.photo (from API), then other fields
    const driverImage =
      order?.driverDetail?.photo ??
      order?.driverDetail?.profileImage ??
      order?.driverDetail?.profile_image ??
      order?.driverDetail?.image ??
      order?.details?.driverImage;

    this.driverNameText = driverName;
    this.driverProfileImageUrl = driverImage ? `${driverImage}` : null;
  }

  private toRad(deg: number): number {
    return deg * Math.PI / 180;
  }



  calculateBearing(startLat: number, startLng: number, destLat: number, destLng: number): number {
    const toRadians = (deg: number) => deg * Math.PI / 180;
    const toDegrees = (rad: number) => rad * 180 / Math.PI;

    const φ1 = toRadians(startLat);
    const φ2 = toRadians(destLat);
    const λ1 = toRadians(startLng);
    const λ2 = toRadians(destLng);

    const y = Math.sin(λ2 - λ1) * Math.cos(φ2);
    const x = Math.cos(φ1) * Math.sin(φ2) -
      Math.sin(φ1) * Math.cos(φ2) * Math.cos(λ2 - λ1);
    const θ = Math.atan2(y, x);

    return (toDegrees(θ) + 360) % 360; // normalize to 0-360
  }

  setDriverVehicleImage(type: number): void {
    const vehicle = trackVehicleImages.filter((item) => item.id == type)[0];
    if (vehicle) {
      this.markerOptions.origin.icon.url = vehicle.icon;
      this.driverIcon.url = vehicle.icon;

      // Update the existing image src if it exists
      if (this.driverIconElement) {
        this.driverIconElement.src = vehicle.icon;
      } else {
        this.markerOptions.origin.icon.url = 'assets/images/tracking/bike_pin.png'
        this.driverIconElement.src = 'assets/images/tracking/bike_pin.png'
      }
    } else {
      // Default image
      const defaultIcon = 'assets/images/tracking/bike_pin.png';
      this.markerOptions.origin.icon.url = defaultIcon;
      this.driverIcon.url = defaultIcon;

      if (this.driverIconElement) {
        this.driverIconElement.src = defaultIcon;
      }
    }
  }

  updateTrackingDoc(id: string) {
    return this.afs.collection(liveTraking).doc(id).valueChanges();
  }

  updateDropoffPoint(id: string) {
    return this.afs.collection(ordersTracking).doc(id).valueChanges();
  }

  ngOnDestroy(): void {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    // Clear the position queue
    this.positionQueue = [];
    this.isAnimating = false;

    // Clear all created markers
    this.clearExistingMarkers();

    if (this.driverSubscription) {
      this.driverSubscription.unsubscribe();
    }

    if (this.dropPointsSubscription) {
      this.dropPointsSubscription.unsubscribe();
    }

    if (this.locationUpdateSubscription) {
      this.locationUpdateSubscription.unsubscribe();
    }

    if (this.routeUpdateSubscription) {
      this.routeUpdateSubscription.unsubscribe();
    }


    console.log(`[API Summary] Total API calls during component life: ${this.apiCallCount}`);

    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  // New method to create markers for drop points
  createMarker(position: google.maps.LatLngLiteral, iconUrl: string): void {
    const marker = new google.maps.Marker({
      position: position,
      map: this.map.googleMap,
      icon: {
        url: iconUrl,
        scaledSize: new google.maps.Size(40, 40) // Adjust size as needed
      }
    });

    // Track the marker for cleanup
    this.createdMarkers.push(marker);
  }

  // Array to track created markers for cleanup
  private createdMarkers: google.maps.Marker[] = [];

  // New method to clear existing markers (if needed)
  clearExistingMarkers(): void {
    // Clear all programmatically created markers
    this.createdMarkers.forEach(marker => {
      marker.setMap(null);
    });
    this.createdMarkers = [];
  }
}